import {
  useEffect,
  useRef, useState
} from 'react';

import { GAEventName, sendGAEvent } from '@rippling/utils/gaUtils';
import { twJoin, twMerge } from 'tailwind-merge';
import Cookies from 'universal-cookie';

import CloseIcon from '../../assets/CloseIcon';
import {
  BaseCfComponent, CfAsset, CfLink,
  Theme
} from '../../types';
import Button, { ButtonVariant } from '../atoms/Button';
import IconButton from '../atoms/IconButton';
import Link from '../atoms/Link';
import TextCombination, { HeadingStyle, TextCombinationProps } from '../atoms/TextCombination';
import CfDebugger from '../CfDebugger';
import CfImage from '../CfImage';
import Form, { FormProps } from './Form';

export enum PopupModalVariant {
  CTA = 'CTA',
  Editorial = 'Editorial',
  Form = 'Form',
  Resource = 'Resource'
}

export enum PopupModalTriggerVariant {
  ExitFrame = 'Exit Frame',
  Timer = 'Timer'
}

export interface PopupModalProps extends BaseCfComponent {
  fields: {
    buttons?: CfLink[];
    form?: FormProps;
    image?: CfAsset;
    showAfter?: number;
    title: TextCombinationProps;
    triggerVariant?: PopupModalTriggerVariant;
    variant: PopupModalVariant;
  };
  isOpen?: boolean;
  onClose?: () => void;
}

const cookies = new Cookies();

const getDialogClasses = (isEditorial: boolean) => {
  if (isEditorial) { return 'ml-auto mr-0 min-h-screen lg:my-0 bg-transparent mb-0 max-w-none'; }

  return 'backdrop:bg-black/70 rounded-lg';
};

const getContainerClasses = (isLarge: boolean, hasImage: boolean, isEditorial: boolean, isResource: boolean) => {
  if (isEditorial) { return 'lg:w-full w-screen lg:max-w-[864px] lg:ml-20 bg-surface-l0 h-full lg:min-h-screen min-h-[calc(100vh-120px)] mt-[120px] lg:mt-0'; }

  const nonEditorialClasses = 'flex relative flex-col lg:flex-row p-3xs bg-surface-l1';
  let widthClass = 'max-w-sm';
  if (hasImage && isLarge) {
    widthClass = 'max-w-3xl';
  } else if (hasImage) {
    widthClass = 'max-w-2xl';
  } else if (isLarge) {
    widthClass = 'max-w-md';
  } else if (isResource) {
    widthClass = 'max-w-sm';
  }

  return twJoin(nonEditorialClasses, widthClass);
};

const getCloseButtonClassNames = (isEditorial: boolean) => {
  if (isEditorial) {
    return 'lg:top-6 lg:-left-6 bg-surface-l0 -top-6 right-6 lg:-translate-x-full lg:translate-y-0 -translate-y-full';
  }

  return 'top-2 right-2';
};

const PopupModal = ({
  className,
  fields,
  isOpen: isControlledOpen,
  onClose,
  sys,
}: PopupModalProps) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const isControlledByLink = isControlledOpen !== undefined;
  const isEditorial = fields.variant === PopupModalVariant.Editorial;
  const isResource = fields.variant === PopupModalVariant.Resource;
  const [isInternalOpen, setIsInternalOpen] = useState(isControlledOpen || false);
  const isOpen = isControlledOpen ?? isInternalOpen;
  const popupId = sys.id;
  const {
    buttons, form, image, showAfter = 4, title, triggerVariant, variant,
  } = fields;
  const isLarge = variant === PopupModalVariant.Form && form && form.fields?.fields?.length > 1;
  const hasImage = Boolean(image);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    toggleModal();
  }, [isOpen]);

  useEffect(() => {
    const isDismissed = cookies.get(`dismissed_${popupId}`);
    if (isControlledByLink || !triggerVariant || isDismissed) {
      return;
    }
    const milliseconds = showAfter * 1000;

    const showModalWithDelay = () => {
      timeoutRef.current = setTimeout(() => {
        setIsInternalOpen(true);
      }, milliseconds);
    };

    const showOnExit = (e: MouseEvent) => {
      if (e.relatedTarget) {
        return;
      }
      showModalWithDelay();
    };

    if (triggerVariant === PopupModalTriggerVariant.ExitFrame) {
      document.addEventListener('mouseleave', showOnExit);
    }

    if (triggerVariant === PopupModalTriggerVariant.Timer) {
      showModalWithDelay();
    }

    return () => {
      document.removeEventListener('mouseleave', showOnExit);
      clearTimeout(timeoutRef.current);
    };
  }, [isInternalOpen]);

  const handleClose = () => {
    cookies.set(`dismissed_${popupId}`, true);
    setIsInternalOpen(false);
    onClose?.();
  };

  const toggleModal = () => {
    if (!dialogRef.current) {
      return;
    }
    if (isOpen && !dialogRef.current.open) {
      dialogRef.current.showModal();
      sendGAEvent({
        event: GAEventName.PopupView,
        popup_name: popupId,
      });

      return;
    }
    if (!isOpen && dialogRef.current.open) {
      dialogRef.current.close();
      sendGAEvent({
        event: GAEventName.PopupClose,
        popup_name: popupId,
      });
    }
  };

  const sendClickEvent = (id: string) => {
    sendGAEvent({
      event: GAEventName.PopupCtaClick,
      popup_cta_name: id,
      popup_name: popupId,
    });
  };

  return (
    <CfDebugger
      className={className}
      sys={sys}
    >
      <dialog
        ref={dialogRef}
        className={twMerge(getDialogClasses(isEditorial), !isOpen && 'hidden')}
        data-theme={isEditorial ? Theme.White : undefined}
        onClose={onClose}
      >
        <div className={twJoin('relative text-left', getContainerClasses(Boolean(isLarge), hasImage, isEditorial, isResource))}>
          <IconButton
            aria-label="Close dialog"
            className={twJoin('absolute cursor-pointer focus-visible:outline-none', getCloseButtonClassNames(isEditorial))}
            icon={<CloseIcon />}
            onClick={handleClose}
          />
          {image && !isEditorial && (
            <CfImage
              className="object-cover w-full h-full"
              containerClassName="rounded-md shrink-0 overflow-hidden lg:basis-1/2"
              {...image}
            />
          )}
          <div className={twJoin('p-2xl flex flex-col items-center justify-center lg:min-h-[472px]', hasImage && !isEditorial && 'lg:basis-1/2')}>
            {title && (
              <TextCombination
                {...title}
                className="mb-lg"
                fields={{
                  ...title.fields,
                  headingStyle: isResource ? HeadingStyle.H4 : HeadingStyle.P1P2,
                }}
              />
            )}
            {(variant === PopupModalVariant.Form || isResource) && form?.fields?.fields && (
              <Form
                {...form}
                className="p-0"
                fields={{
                  ...form.fields,
                  title: '',
                }}
                popupId={popupId}
              />
            )}
            {variant === PopupModalVariant.CTA && buttons?.length && (
              <div className="flex lg:flex-row w-full gap-sm items-center">
                <Button
                  cfLinkData={buttons[0]}
                  className={twJoin(buttons.length === 1 && 'w-full')}
                  onClick={() => sendClickEvent(buttons[0].sys.id)}
                  variant={ButtonVariant.Filled}
                />
                {buttons[1] && (
                  <Link
                    cfLinkData={buttons[1]}
                    onClick={
                      () => sendClickEvent(buttons[1].sys.id)
                    }
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </dialog>
    </CfDebugger>
  );
};

export default PopupModal;
